import { useState, useEffect, useRef } from 'react';
import { Clock, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DebitoInmediatoPayment({ order, onSuccess, onError }) {
  const [step, setStep] = useState(1); // 1: Datos, 2: Token
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(null);
  const [tokenInput, setTokenInput] = useState('');
  const [waitSeconds, setWaitSeconds] = useState(0);
  const tokenRef = useRef(null);

  // Paso 3: el banco aprobó el débito, pero el pago se confirma solo cuando el
  // crédito aparece en la cuenta. Consultamos el estado de la orden hasta que
  // el verificador la apruebe (o la cierre como fallida).
  const esperarAcreditacion = async (debitResult) => {
    const started = Date.now();
    const poll = setInterval(async () => {
      setWaitSeconds(Math.floor((Date.now() - started) / 1000));
      try {
        const res = await axios.get(`${BACKEND_URL}/api/public/orders/${order.order_id}`);
        const st = res.data?.status;
        if (st === 'paid' || st === 'completed') {
          clearInterval(poll);
          onSuccess({
            method: 'Débito Inmediato',
            endtoend: debitResult.endtoend,
            referencia: debitResult.referencia,
          });
        } else if (st === 'failed' || st === 'expired') {
          clearInterval(poll);
          onError('El banco aprobó el débito pero el crédito no se reflejó en la cuenta. '
            + 'No se te cobró el pedido; ya estamos reclamando la operación al banco.');
        }
      } catch (e) {
        /* reintenta en el próximo ciclo */
      }
      if (Date.now() - started > 15 * 60 * 1000) {
        clearInterval(poll);
      }
    }, 5000);
  };
  
  const [clientData, setClientData] = useState({
    identificacion: '',
    banco: '',
    cuenta: '',
    telefono: ''
  });

  // Timer de 2 minutos
  useEffect(() => {
    if (timer !== null && timer > 0) {
      const interval = setInterval(() => {
        setTimer(t => t - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      // Token expirado
      setStep(1);
      setTimer(null);
      setTokenInput('');
    }
  }, [timer]);

  const solicitarToken = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/public/orders/${order.order_id}/debit/request-token`,
        {
          identificacion: clientData.identificacion,
          banco: clientData.banco,
          cuenta: clientData.cuenta,
          telefono: clientData.telefono,
          ip: '192.168.1.1'
        }
      );
      
      if (response.data.success) {
        setStep(2);
        setTimer(120); // 2 minutos
        setTimeout(() => tokenRef.current?.focus(), 100);
      } else {
        onError(response.data.mensaje || 'Error al solicitar token');
      }
    } catch (error) {
      onError(error.response?.data?.detail || 'Error al solicitar token');
    } finally {
      setLoading(false);
    }
  };

  const ejecutarCobro = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/public/orders/${order.order_id}/debit/execute`,
        {
          token: tokenInput,
          ip: '192.168.1.1'
        }
      );
      
      if (response.data.pending_settlement) {
        // El 0000 del banco no acredita: esperamos ver el movimiento real.
        setStep(3);
        setTimer(null);
        esperarAcreditacion(response.data);
      } else if (response.data.success) {
        onSuccess({
          method: 'Débito Inmediato',
          endtoend: response.data.endtoend,
          referencia: response.data.referencia
        });
      } else if (response.data.pending) {
        // Transacción pendiente (interbancario)
        onError('Transacción enviada al BCV. Verificar en unos minutos.');
      } else {
        onError(
          response.data.codigo
            ? `${response.data.mensaje || 'Error al procesar el pago'} (código ${response.data.codigo})`
            : (response.data.mensaje || 'Error al procesar el pago')
        );
      }
    } catch (error) {
      onError(error.response?.data?.detail || 'Error al ejecutar el cobro');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Alerta de 2 minutos */}
      {step === 2 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-yellow-900">
                ⏰ Token válido por 2 minutos
              </p>
              <p className="text-xs text-yellow-800 mt-1">
                Ingresa el token recibido por SMS antes de que expire
              </p>
            </div>
            {timer !== null && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-xl ${
                timer <= 30 ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-green-100 text-green-700'
              }`}>
                <Clock className="w-5 h-5" />
                {formatTime(timer)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* PASO 1: Datos del Cliente */}
      {step === 1 && (
        <form onSubmit={solicitarToken} className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm font-medium text-blue-900">
              💳 Ingresa tus datos para solicitar el token de autorización
            </p>
          </div>

          {/* Identificación */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Identificación (12 caracteres) *
            </label>
            <input
              type="text"
              value={clientData.identificacion}
              onChange={(e) => setClientData({...clientData, identificacion: e.target.value.toUpperCase()})}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500"
              placeholder="V00012345678 o J00123456789"
              maxLength={12}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Ejemplo: V00012345678 (12 caracteres exactos)
            </p>
          </div>

          {/* Banco */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Tu Banco *
            </label>
            <select
              value={clientData.banco}
              onChange={(e) => setClientData({...clientData, banco: e.target.value})}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500"
              required
            >
              <option value="">Selecciona tu banco...</option>
              <option value="0138">0138 - Banco Plaza</option>
              <option value="0102">0102 - Banco de Venezuela</option>
              <option value="0105">0105 - Banco Mercantil</option>
              <option value="0108">0108 - Banco Provincial</option>
              <option value="0114">0114 - Bancaribe</option>
              <option value="0134">0134 - Banesco</option>
              <option value="0151">0151 - BFC</option>
              <option value="0128">0128 - Banco Caroní</option>
              <option value="0174">0174 - Banplus</option>
              <option value="0175">0175 - Banco Bicentenario</option>
            </select>
          </div>

          {/* Cuenta o Teléfono */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Número de Cuenta (20 dígitos)
            </label>
            <input
              type="text"
              value={clientData.cuenta}
              onChange={(e) => setClientData({...clientData, cuenta: e.target.value.replace(/\D/g, '')})}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500"
              placeholder="01380011130110248648"
              maxLength={20}
            />
            <p className="text-xs text-gray-500 mt-1">Opcional si tienes teléfono</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Teléfono (11 dígitos)
            </label>
            <input
              type="text"
              value={clientData.telefono}
              onChange={(e) => setClientData({...clientData, telefono: e.target.value.replace(/\D/g, '')})}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500"
              placeholder="04241234567"
              maxLength={11}
            />
            <p className="text-xs text-gray-500 mt-1">Opcional si tienes cuenta</p>
          </div>

          {/* Botón Solicitar Token */}
          <button
            type="submit"
            disabled={loading || !clientData.identificacion || !clientData.banco || (!clientData.cuenta && !clientData.telefono)}
            className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader className="w-5 h-5 animate-spin" />
                Solicitando Token...
              </span>
            ) : (
              '📤 SOLICITAR TOKEN POR SMS'
            )}
          </button>
        </form>
      )}

      {/* PASO 2: Ingresar Token */}
      {step === 2 && (
        <form onSubmit={ejecutarCobro} className="space-y-4">
          <div className="bg-green-50 border border-green-300 rounded-lg p-4">
            <p className="text-sm font-bold text-green-900 mb-2">
              ✅ Token solicitado exitosamente
            </p>
            <p className="text-xs text-green-700">
              Recibirás un SMS con el código de 8 dígitos en unos segundos
            </p>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Ingresa el Token de 8 dígitos:
            </label>
            <input
              ref={tokenRef}
              type="text"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value.replace(/\D/g, ''))}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && tokenInput.length === 8) {
                  ejecutarCobro(e);
                }
              }}
              maxLength={8}
              className="w-full px-4 py-4 border-2 border-blue-400 rounded-xl text-center text-3xl font-mono font-bold focus:ring-4 focus:ring-blue-200 focus:border-blue-600"
              placeholder="● ● ● ● ● ● ● ●"
              disabled={loading || timer === 0}
              autoComplete="off"
              required
            />
            <p className="text-xs text-gray-500 mt-1 text-center">
              Presiona Enter para continuar
            </p>
          </div>

          <button
            type="submit"
            disabled={loading || tokenInput.length !== 8 || timer === 0}
            className="w-full py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader className="w-5 h-5 animate-spin" />
                Procesando Pago...
              </span>
            ) : (
              '✓ CONFIRMAR PAGO'
            )}
          </button>

          {/* Botón para solicitar nuevo token si expiró */}
          {timer === 0 && (
            <button
              onClick={() => {
                setStep(1);
                setTimer(null);
                setTokenInput('');
              }}
              className="w-full py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600"
            >
              🔄 Solicitar Nuevo Token
            </button>
          )}
        </form>
      )}

      {/* Paso 3: esperando que el crédito se refleje en la cuenta */}
      {step === 3 && (
        <div className="text-center py-8" data-testid="di-awaiting-settlement">
          <Loader className="w-10 h-10 mx-auto mb-4 animate-spin text-blue-600" />
          <p className="font-semibold text-gray-800 mb-1">Confirmando tu pago con el banco</p>
          <p className="text-sm text-gray-600 max-w-sm mx-auto">
            El banco aprobó el débito y estamos verificando que el dinero se acreditara.
            Esto suele tomar menos de un minuto. No cierres esta ventana.
          </p>
          <p className="text-xs text-gray-400 mt-3">{waitSeconds}s</p>
        </div>
      )}

      {/* Resumen de datos ingresados */}
      {step === 2 && (
        <div className="bg-gray-50 rounded-lg p-4 text-xs">
          <p className="font-semibold text-gray-700 mb-2">Datos de tu pago:</p>
          <div className="space-y-1 text-gray-600">
            <p>• Identificación: {clientData.identificacion}</p>
            <p>• Banco: {clientData.banco}</p>
            {clientData.cuenta && <p>• Cuenta: {clientData.cuenta}</p>}
            {clientData.telefono && <p>• Teléfono: {clientData.telefono}</p>}
            <p>• Monto: {order.currency} {order.amount.toFixed(2)}</p>
          </div>
        </div>
      )}
    </div>
  );
}
